import {
    DateTimeFormatter,
    DateTimeFormatterBuilder,
    ResolverStyle,
    IsoChronology,
    LocalDateTime,
    LocalDate,
    LocalTime,
    Instant,
    ZoneId,
    ChronoUnit,
    ZoneOffset,
    ZonedDateTime,
    OffsetDateTime,
} from '@js-joda/core';
import Logger from './logger/logger.js'

const log = new Logger('lib/utils')

const DF = new DateTimeFormatterBuilder()
    .parseCaseInsensitive()
    .append(DateTimeFormatter.ISO_LOCAL_DATE)
    .toFormatter(ResolverStyle.STRICT).withChronology(IsoChronology.INSTANCE);

const DFU = DateTimeFormatter.ofPattern("yyyy-MM-dd");

const DTF = new DateTimeFormatterBuilder()
    .parseCaseInsensitive()
    .append(DateTimeFormatter.ISO_LOCAL_DATE)
    .appendLiteral(' ')
    .append(DateTimeFormatter.ISO_LOCAL_TIME)
    .toFormatter(ResolverStyle.STRICT).withChronology(IsoChronology.INSTANCE);

const DTFU = DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss");

const TFU = DateTimeFormatter.ofPattern('HH:mm')

function parse_date(text)
{
    if (!text) return null;
    return LocalDateTime.parse(text, DF)
}

function format_date(dt)
{
    if (!dt) return null;
    return dt.format(DFU)
}

function parse_date_time(text)
{
    if (!text) return null;
    try
    {
        let date_time = LocalDateTime.parse(text, DTF)
        if (date_time)
        {
            return date_time
        }
    }
    catch (e)
    {
        // log.d('parse_date_time failed DTF', e);
    }
    return LocalDateTime.parse(text)
}

function parse_local_to_utc(dt)
{
    if (!dt) return null;

    const local = LocalDateTime.parse(dt);

    return ZonedDateTime
        .of(local, ZoneId.systemDefault())
        .withZoneSameInstant(ZoneOffset.UTC)
        .toLocalDateTime()
        .toString();
}

function parse_utc_to_local(dt)
{
    if (!dt) return null;

    const utc = LocalDateTime.parse(dt);

    return ZonedDateTime
        .of(utc, ZoneOffset.UTC)
        .withZoneSameInstant(ZoneId.systemDefault())
        .toLocalDateTime();
}

function display_utc_local(dt) {
    if (!dt) return null;

    return format_date_time(parse_utc_to_local(dt));
}

function format_date_time(dt)
{
    if (!dt) return null;
    return dt.format(DTFU)
}

function format_date_time_to_api(dt)
{
    return `${dt.toLocalDate()} ${dt.toLocalTime().toString().substring(0,5)}`;
}

function format_date_time_from_api(dt)
{
    return LocalDateTime.parse(dt.replace(' ', 'T'));
}

function format_datetime_zone(dt) {
    if (!dt) return null;

    return LocalDateTime
        .ofInstant(Instant.parse(dt), ZoneId.SYSTEM)
        .format(DTF);
}

function format_datetime_offset(dt)
{
    if (!dt) return null;

    return OffsetDateTime
        .parse(dt)
        .format(DTFU);
}

function get_now()
{
    return LocalDateTime.now()
}

function get_now_utc()
{
    return LocalDateTime.now(ZoneOffset.UTC);
}

function get_today()
{
    return LocalDate.now().format(DFU)
}

function get_tomorrow()
{
    return LocalDate.now().plusDays(1).format(DFU);
}

function get_year()
{
    return LocalDate.now().year();
}

function get_next_full_hour()
{
    const next_hour = LocalTime.now().plusHours(1).withMinute(0).withSecond(0);
    return next_hour.format(TFU);
}

function is_image(url)
{
    if (!url)
        return false;
    return url.match(/\.(jpeg|jpg|gif|png|tiff)$/) != null;
}

function is_pdf(url)
{
    if (!url)
        return false;
    return url.match(/\.(pdf)$/) != null;
}

async function upload_mp(path, data)
{
    const form_data = new FormData();
    for (const key in data)
    {
        if (data.hasOwnProperty(key))
        {
            form_data.append(key, data[key]);
        }
    }
    let dispatcher_token = localStorage.getItem('dispatcher_token');
    form_data.append('token', dispatcher_token);
    log.d('upload_mp', form_data);

    let url = "/admin/dispatcherapi" + path
    let resp = await fetch(url,
        { method: 'POST', body: form_data }
    );
    if (!resp.ok)
    {
        return null
    }
    let resp_data = await resp.json();
    return resp_data;
}

function naturalComparator(a, b)
{
    if (a === null && b === null)
    {
        return 0;
    } else if (a === null)
    {
        return 1;
    } else if (b === null)
    {
        return -1;
    }

    // If the lengths are different, sort by length
    if (a.length !== b.length)
    {
        return a.length - b.length;
    }

    // If the lengths are the same, sort lexicographically
    return a.localeCompare(b);
}

async function delay(ms)
{
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function set_dark_mode(dark_mode, vuetify_theme)
{
    if (dark_mode === undefined || dark_mode === null)
    {
        let dark_system = window.matchMedia('(prefers-color-scheme: dark)').matches
        set_dark_mode(dark_system, vuetify_theme)
        return
    }
    if (dark_mode)
    {
        if (!document.body.classList.contains('dark'))
        {
            document.body.classList.add('dark');
        }
        if (vuetify_theme)
        {
            vuetify_theme.change('taxi_theme_dark')
        }
    }
    else
    {
        if (document.body.classList.contains('dark'))
        {
            document.body.classList.remove('dark');
        }
        if (vuetify_theme)
        {
            vuetify_theme.change('taxi_theme')
        }
    }
}

function toggle_dark_mode(vuetify_theme)
{
    if (document.body.classList.contains('dark'))
    {
        set_dark_mode(false, vuetify_theme)
    }
    else
    {
        set_dark_mode(true, vuetify_theme)
    }
}

function get_date_iso(add_days = 0)
{
    const now = LocalDateTime.now();
    return now.plusDays(add_days).format(DateTimeFormatter.ISO_LOCAL_DATE)
}

function get_datetime_iso(add_days = 0)
{
    const now = LocalDateTime.now().truncatedTo(ChronoUnit.SECONDS)
    return now.plusDays(add_days).format(DateTimeFormatter.ISO_LOCAL_DATE_TIME)
}

function get_datetime_iso_00(add_days = 0)
{
    const now = LocalDateTime.now().truncatedTo(ChronoUnit.DAYS)
    return now.plusDays(add_days).format(DateTimeFormatter.ISO_LOCAL_DATE_TIME)
}

function var_val_display_func(t, data)
{
    log.d("var_val_display_func", data)
    if (data.val === null)
    {
        return t('config.var.val.null');
    }
    if (data.choices)
    {
        if (data.type in { 'b': 1, 'B': 1 } && data.val === '')
        {
            return '';
        }
        return t('config.var.val.' + data.var + '.' + data.val);
    }
    if (data.type in { 'i': 1, 'd': 1, 's': 1, 'S': 1, 'J': 1 })
    {
        return data.val;
    }
    if (data.type in { 'b': 1, 'B': 1 })
    {
        return t('config.var.val.' + data.var + '.' + data.val);
    }
    if (data.type === 't')
    {
        return seconds_to_string(data.val);
    }
    if (data.type === ';')
    {
        let ret = Array.isArray(data.val) ? data.val : data.val.split(';')
        ret = ret.map(item => item.trim())
            .filter(item => item.length > 0);
        return ret.join('; ')
    }
    return data.val;
}

function seconds_to_string(seconds)
{
    if (seconds === null || seconds === undefined || seconds === '')
    {
        return '';
    }
    seconds = Number(seconds);
    const days = Math.floor(seconds / (3600 * 24));
    const hours = Math.floor((seconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return days > 0
        ? `${days} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        : `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

let now_in_seconds = () => Instant.now().epochSecond()

function timestamp_now_diff_string(timestamp)
{
    const current_time = now_in_seconds();
    let time_difference = current_time - timestamp;
    if (time_difference < 0)
    {
        time_difference= -time_difference
    }
    return seconds_to_string(time_difference)
}

function get_days_hours_delta_from_datetime(t, dt, end_dt = null) {
    const from = format_date_time_from_api(dt);
    let to = get_now();
    if(end_dt) {
        to = format_date_time_from_api(end_dt);
    }
    const total_hours = Math.abs(ChronoUnit.HOURS.between(from, to));

    const days = Math.floor(total_hours / 24);
    let days_key = 'common.days';
    if(days === 1) {
        days_key = 'common.day';
    }
    const hours = total_hours % 24;

    return `${days} ${t(days_key)} ${hours} ${t('common.hours')}`;
}

function truncate(str, maxLength = 30) {
    if (str.length > maxLength) {
        return str.substring(0, maxLength) + '\u2026';  // Unicode for ellipsis
    }
    return str;
}

function generate_password(length = 16) {
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const special = '!@#$%^&*()_+';
    const all_chars = lower + upper + digits + special;

    let password = '';
    password += lower[Math.floor(Math.random() * lower.length)];
    password += upper[Math.floor(Math.random() * upper.length)];
    password += digits[Math.floor(Math.random() * digits.length)];
    password += special[Math.floor(Math.random() * special.length)];

    for (let i = password.length; i < length; i++) {
        password += all_chars[Math.floor(Math.random() * all_chars.length)];
    }
    password = password.split('').sort(() => Math.random() - 0.5).join('');

    return password;
}

function meters_to_kilometers(meters) {
    return Number(meters || 0) / 1000;
}

export
{
    DF,
    DFU,
    DTF,
    DTFU,
    TFU,
    get_now,
    get_now_utc,
    get_today,
    get_tomorrow,
    get_year,
    get_next_full_hour,
    parse_date,
    format_date,
    parse_date_time,
    parse_local_to_utc,
    parse_utc_to_local,
    display_utc_local,
    format_date_time,
    format_date_time_to_api,
    format_date_time_from_api,
    format_datetime_zone,
    format_datetime_offset,
    is_image,
    is_pdf,
    upload_mp,
    naturalComparator,
    delay,
    set_dark_mode,
    toggle_dark_mode,
    get_date_iso,
    get_datetime_iso,
    get_datetime_iso_00,
    var_val_display_func,
    seconds_to_string,
    timestamp_now_diff_string,
    get_days_hours_delta_from_datetime,
    truncate,
    generate_password,
    meters_to_kilometers,
};
